import React, { useMemo, useState, useEffect } from 'react';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import Chip from 'apollo-react/components/Chip';
import Collapse from 'apollo-react/components/Collapse';
import Typography from 'apollo-react/components/Typography';
import CircularProgress from 'apollo-react/components/CircularProgress';
import {
  formatAttachmentSize,
  normalizeProfileFileDescriptor,
} from '../profileAttachmentUtils';
import {
  fetchProfileFileObjectUrl,
  openProfileFilePreviewTab,
  downloadProfileFileBlob,
} from '../profileFileAxiosAccess';
import buttonStyles from '../../../components/common/Button.module.css';

/** @typedef {{ id:number, slotKey:string, reason?:string, supersededAt?:string, actorEndUserId?:number|null, file:{ id:number, originalName?:string, mimeType?:string, fileSize?:number }|null }} AttachmentRevisionApi */

function isImageMime(m) {
  return typeof m === 'string' && m.toLowerCase().startsWith('image/');
}

function clickLinkStyle() {
  return {
    cursor: 'pointer',
    textDecoration: 'underline',
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
  };
}

export default function ProfileAttachmentBlock({
  file,
  slotKey,
  attachmentRevisions = [],
  compact = false,
  showPriorVersions,
}) {
  const normalized = normalizeProfileFileDescriptor(file);
  const [openPrior, setOpenPrior] = useState(false);
  const [embedSrc, setEmbedSrc] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);

  const resolvedShowPrior =
    showPriorVersions !== undefined ? !!showPriorVersions : !compact;

  const priorSorted = useMemo(() => {
    if (!slotKey || !resolvedShowPrior || !Array.isArray(attachmentRevisions))
      return [];
    const key = String(slotKey);
    const rows = attachmentRevisions.filter((r) => r && r.slotKey === key);
    return [...rows].sort((a, b) => {
      const ta = a.supersededAt ? Date.parse(a.supersededAt) : 0;
      const tb = b.supersededAt ? Date.parse(b.supersededAt) : 0;
      return tb - ta;
    });
  }, [attachmentRevisions, slotKey, resolvedShowPrior]);

  const showEmbeddedPreview =
    normalized && isImageMime(normalized.mimeType) && !compact;

  useEffect(() => {
    if (!showEmbeddedPreview || !normalized?.id) {
      setEmbedSrc(null);
      setEmbedLoading(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setEmbedLoading(true);
    setEmbedSrc(null);
    (async () => {
      try {
        objectUrl = await fetchProfileFileObjectUrl(normalized.id, 'preview');
        if (!cancelled) {
          setEmbedSrc(objectUrl);
        }
      } catch {
        if (!cancelled) setEmbedSrc(null);
      } finally {
        if (!cancelled) setEmbedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [normalized?.id, showEmbeddedPreview]);

  if (!normalized) {
    return (
      <Typography variant="bodySmall" color="text.secondary">
        —
      </Typography>
    );
  }

  const metaLine = [normalized.mimeType, formatAttachmentSize(normalized.size)]
    .filter(Boolean)
    .join(' · ');

  if (compact) {
    return (
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
        <Chip
          label={normalized.originalName}
          size="small"
          clickable
          onClick={() => {
            void openProfileFilePreviewTab(normalized.id);
          }}
          title="Preview"
        />
        {metaLine ? (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            component="span"
          >
            {metaLine}
          </Typography>
        ) : null}
        <Typography
          variant="bodySmall"
          component="button"
          type="button"
          style={clickLinkStyle()}
          onClick={() => {
            void downloadProfileFileBlob(
              normalized.id,
              normalized.originalName
            );
          }}
        >
          Download
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="profile-attachment-block"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: '#fafafa',
      }}
    >
      <Typography
        variant="body"
        fontWeight={500}
        sx={{ wordBreak: 'break-word' }}
      >
        {normalized.originalName}
      </Typography>
      {metaLine ? (
        <Typography variant="bodySmall" color="text.secondary">
          {metaLine}
        </Typography>
      ) : null}
      {showEmbeddedPreview ? (
        <Box mt={1.5}>
          {embedLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          ) : embedSrc ? (
            <Box
              component="button"
              type="button"
              onClick={() => {
                void openProfileFilePreviewTab(normalized.id);
              }}
              sx={{
                border: 'none',
                padding: 0,
                background: 'none',
                cursor: 'pointer',
                display: 'block',
                width: '100%',
              }}
              title="Open full preview"
            >
              <Box
                component="img"
                src={embedSrc}
                alt={`Preview ${normalized.originalName}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 220,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mt: 0.5,
                }}
              />
            </Box>
          ) : null}
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Opens full size from Preview for PDF files.
          </Typography>
        </Box>
      ) : null}

      <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
        <Button
          variant="secondary"
          size="small"
          className={buttonStyles.button}
          type="button"
          onClick={() => {
            void openProfileFilePreviewTab(normalized.id);
          }}
        >
          Preview
        </Button>
        <Button
          variant="primary"
          size="small"
          className={buttonStyles.button}
          type="button"
          onClick={() => {
            void downloadProfileFileBlob(
              normalized.id,
              normalized.originalName
            );
          }}
        >
          Download
        </Button>
      </Box>

      {priorSorted.length ? (
        <Box mt={1.5}>
          <Button
            type="button"
            variant="secondary"
            size="small"
            className={`${buttonStyles.button} p-0`}
            style={{ minWidth: 0, textTransform: 'none' }}
            onClick={() => setOpenPrior((x) => !x)}
          >
            Previous versions ({priorSorted.length}) {openPrior ? '▲' : '▼'}
          </Button>
          <Collapse in={openPrior}>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                mt: 1.5,
                pl: 2,
                borderLeft: '2px solid',
                borderColor: 'divider',
              }}
            >
              {priorSorted.map((r) => {
                const f = normalizeProfileFileDescriptor(r.file);
                if (!f) return null;
                return (
                  <Box component="li" key={r.id} sx={{ mb: 1.5 }}>
                    <Typography
                      variant="bodySmall"
                      color="text.secondary"
                      component="span"
                    >
                      {r.reason || 'replaced'}
                      {r.supersededAt
                        ? ` · ${new Date(r.supersededAt).toLocaleString()}`
                        : ''}
                    </Typography>
                    <Typography variant="bodySmall">
                      {f.originalName}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Typography
                        variant="bodySmall"
                        component="button"
                        type="button"
                        style={clickLinkStyle()}
                        onClick={() => {
                          void openProfileFilePreviewTab(f.id);
                        }}
                      >
                        Preview
                      </Typography>
                      <Typography
                        variant="bodySmall"
                        component="button"
                        type="button"
                        style={clickLinkStyle()}
                        onClick={() => {
                          void downloadProfileFileBlob(f.id, f.originalName);
                        }}
                      >
                        Download
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

/** List-only block for superseded uploads (no current attachment row). */
export function AttachmentSlotRevisionHistory({
  slotKey,
  attachmentRevisions = [],
  title = 'Archived uploads',
}) {
  const [open, setOpen] = useState(false);

  const priorSorted = useMemo(() => {
    if (!slotKey || !Array.isArray(attachmentRevisions)) return [];
    const key = String(slotKey);
    const rows = attachmentRevisions.filter((r) => r && r.slotKey === key);
    return [...rows].sort((a, b) => {
      const ta = a.supersededAt ? Date.parse(a.supersededAt) : 0;
      const tb = b.supersededAt ? Date.parse(b.supersededAt) : 0;
      return tb - ta;
    });
  }, [attachmentRevisions, slotKey]);

  if (!priorSorted.length) return null;

  return (
    <Box
      mt={1.5}
      pt={1.5}
      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Button
        type="button"
        variant="secondary"
        size="small"
        className={`${buttonStyles.button} p-0`}
        style={{ minWidth: 0, textTransform: 'none' }}
        onClick={() => setOpen((x) => !x)}
      >
        {title} ({priorSorted.length}) {open ? '▲' : '▼'}
      </Button>
      <Collapse in={open}>
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 1.5,
            pl: 2,
            borderLeft: '2px solid',
            borderColor: 'divider',
          }}
        >
          {priorSorted.map((r) => {
            const f = normalizeProfileFileDescriptor(r.file);
            if (!f) return null;
            return (
              <Box component="li" key={r.id} sx={{ mb: 1.5 }}>
                <Typography
                  variant="bodySmall"
                  color="text.secondary"
                  component="span"
                >
                  {r.reason || 'replaced'}
                  {r.supersededAt
                    ? ` · ${new Date(r.supersededAt).toLocaleString()}`
                    : ''}
                </Typography>
                <Typography variant="bodySmall">{f.originalName}</Typography>
                <Box display="flex" gap={1}>
                  <Typography
                    variant="bodySmall"
                    component="button"
                    type="button"
                    style={clickLinkStyle()}
                    onClick={() => {
                      void openProfileFilePreviewTab(f.id);
                    }}
                  >
                    Preview
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    component="button"
                    type="button"
                    style={clickLinkStyle()}
                    onClick={() => {
                      void downloadProfileFileBlob(f.id, f.originalName);
                    }}
                  >
                    Download
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
